import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators, FormArray, FormControl } from '@angular/forms'
import { Subject, Observable, of } from 'rxjs'
import { BasePostForm } from './base-post-form.component'
import { PostActions } from 'actions/post-actions'
import { Post } from 'types'
import { get } from 'lodash-es'
import { DeclineReasonQueries, DeclineReason } from 'src/graphql/queries/decline-reason.queries'

@Component({
  selector: 'decline-post-form',
  styles: [`
    label {
      font-weight: normal;
    }
  `],
  template: `
    <h4>Decline this submission</h4>
    <form [formGroup]="declinePostForm" (ngSubmit)="onSubmit()">
      <p class="h4-subheader">Please let us know why you are declining this submission. All Verblio writers can see your declined content and learn from your feedback, so be as specific as possible.</p>

      <div class="mt-3 d-flex flex-row justify-content-start align-items-baseline">
        <h5 class="m-0">
          What's the reason for your decline?<span class="text-danger">*</span>
        </h5>
        <div class="ml-2 small font-weight-light">(select all that apply)</div>
      </div>

      <ul class="mt-2 list-unstyled">
        <li *ngFor="let declineReason of declineReasons$ | async">
          <label>
            <input type="checkbox" [value]="true" (change)="toggleDeclineReasonSelected(declineReason.id, $event.target.checked)"/>
            {{declineReason.reason}}
          </label>
        </li>
      </ul>

      <div class="d-flex flex-row justify-content-start align-items-baseline">
        <h5 class="m-0">
          What feedback do you have for {{post.writer.shortDisplayName || 'the writer'}}?<span class="text-danger">*</span>
        </h5>
        <div class="ml-2 small font-weight-light">(This will be visible to all writers so they can learn what you like)</div>
      </div>

      <p class="small font-weight-light tiny-bottom-buffer">At least 10 characters</p>
      <textarea style="resize:none;" formControlName="comments" style="width: 100%; max-width: 100%" rows="3"></textarea>

      <!--<rating-stars class="mb-3" [suppressMobile]="true" [value]="rating" (onChange)="rating = $event"></rating-stars>-->

      <p class="mt-3 small">
        By clicking "Confirm decline" you acknowledge that ownership of this content remains with the writer, who may
        publish this work elsewhere. If you use this piece after clicking "Confirm decline", you will be plagiarizing
        the content and in violation of Copyright laws as well as our Terms of Service.
      </p>

      <div class="d-flex flex-row justify-content-between">
        <button type="submit" class="btn btn-dark" [disabled]="!declinePostForm.valid || submitted">
          <span *ngIf="!submitted">Confirm decline</span>
          <span *ngIf="submitted">Please wait...</span>
        </button>

        <button type="button" (click)="onClose()" class="btn btn-default">Cancel</button>
      </div>

      <p class="mt-3 small font-weight-light">
        <span class="text-danger">*</span>
        Feedback is required so that we can send you better content
      </p>
    </form>
    <rate-post-modal *ngIf="ratePostModalVisible && allowRatePostModal()" [post]="post" action="decline" (done)="ratePostSubject.next(post); ratePostSubject.complete()"></rate-post-modal>
  `
})
export class DeclinePostForm extends BasePostForm implements OnInit {
  declinePostForm: FormGroup;
  ratePostModalVisible: boolean = false;
  ratePostSubject: Subject<Post> = new Subject<Post>();
  rating: number;
  declineReasons$: Observable<DeclineReason[]> = of([]);

  constructor(
    private formBuilder: FormBuilder,
    private postActions: PostActions,
    private queries: DeclineReasonQueries
  ) {
    super()
  }

  ngOnInit(): void {
    this.declinePostForm = this.formBuilder.group({
      comments: ['', Validators.compose([Validators.required, Validators.minLength(10)])],
      declineReasons: [[], Validators.required]
    })

    this.declineReasons$ = this.queries.declineReasons()

    this.declinePostForm.valueChanges.subscribe(() => {
      this.setDirty(true)
    })

    this.rating = this.post.appRating?.value
  }

  onSubmit(): void {
    if (!this.submitted) {
      this.submitted = true

      // Needed to populate the rating modal stars
      if (this.rating) this.post.appRating = { ...this.post.appRating, value: this.rating }

      const { comments, declineReasons } = this.declinePostForm.value
      if (this.allowRatePostModal()) {
        this.ratePostModalVisible = true
        this.postActions.declinePost(this.customer.id, this.post.id, this.post.keyword.kind, comments, declineReasons, this.rating, this.ratePostSubject.toPromise()).then(() => {
          this.setDirty(false)
          this.submitted = false
          this.ratePostModalVisible = false
          this.onClose()
          this.deprecatedStoreChange.emit()
        })

      } else {
        this.postActions.declinePost(this.customer.id, this.post.id, this.post.keyword.kind, comments, declineReasons, this.rating).then(() => {
          this.setDirty(false)
          this.submitted = false
          this.onClose()
          this.deprecatedStoreChange.emit()
        })
      }
    }
  }

  allowRatePostModal(): boolean {
    return !this.post.appRating || (this.post.appRating.value <= 2 && !this.post.writerBlocked)
  }

  toggleDeclineReasonSelected(id: number, selected: boolean): void {
    const currentReasons = this.declinePostForm.get('declineReasons').value

    const newReasons = selected ? currentReasons.concat(id) : currentReasons.filter(r => r !== id)

    this.declinePostForm.get('declineReasons').patchValue(newReasons)
  }

}
